import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { ThecData } from '../types';
import { latest, pctChange, previous } from '../helpers';
import { getMultiGoalStatus, getThresholdGoalState } from '../../statusUtils';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function JobPlacementCard({ data }: { data: ThecData }) {
  const theme = useMantineTheme();
  const placements = data.placements;
  const cur = latest(placements);
  const prev = previous(placements);

  const totalPlaced = data.placementsAllTimeTotal;
  const totalCompleted = data.completionsAllTimeTotal;
  const placementRate = totalCompleted > 0 ? (totalPlaced / totalCompleted) * 100 : 0;

  const goalRate = 50; // by end of Year 5 (shown as a directional goal)
  const status = getMultiGoalStatus([
    getThresholdGoalState({ value: placementRate, goal: goalRate, direction: 'min' }),
  ]);

  const parseQuarterLabel = (label: string): { year: number; quarter: 1 | 2 | 3 | 4 } | null => {
    const m1 = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (m1) return { year: Number(m1[2]), quarter: Number(m1[1]) as 1 | 2 | 3 | 4 };
    const m2 = label.match(/^(\d{4})\s+Q([1-4])$/);
    if (m2) return { year: Number(m2[1]), quarter: Number(m2[2]) as 1 | 2 | 3 | 4 };
    return null;
  };

  const curMeta = parseQuarterLabel(cur.quarter);
  const curYear = curMeta?.year ?? null;
  const curQuarterNum = curMeta?.quarter ?? null;

  const ytdPlaced =
    curYear === null || curQuarterNum === null
      ? null
      : placements
          .filter((p) => {
            const m = parseQuarterLabel(p.quarter);
            return m?.year === curYear && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.placedInTulsa, 0);

  const prevYearYtdPlaced =
    curYear === null || curQuarterNum === null
      ? null
      : placements
          .filter((p) => {
            const m = parseQuarterLabel(p.quarter);
            return m?.year === curYear - 1 && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.placedInTulsa, 0);

  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqPlacedPct = pctChange(cur.placedInTulsa, prev?.placedInTulsa ?? null);
  const qoqPlacedText =
    qoqPlacedPct === null ? '—' : arrow(qoqPlacedPct) ? `${arrow(qoqPlacedPct)}${Math.abs(qoqPlacedPct).toFixed(2)}% QoQ` : '0.00% QoQ';

  const yoyPlacedPct = ytdPlaced === null || prevYearYtdPlaced === null ? null : pctChange(ytdPlaced, prevYearYtdPlaced);
  const yoyPlacedText =
    yoyPlacedPct === null ? '—' : arrow(yoyPlacedPct) ? `${arrow(yoyPlacedPct)}${Math.abs(yoyPlacedPct).toFixed(2)}% YoY` : '0.00% YoY';

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const completions = data.completions;
  const completedByQuarter = new Map(completions.map((p) => [p.quarter, p.completed]));
  const placementsSeriesTotal = placements.reduce((sum, p) => sum + p.placedInTulsa, 0);
  const completionsSeriesTotal = completions.reduce((sum, p) => sum + p.completed, 0);
  const priorPlaced = Math.max(0, totalPlaced - placementsSeriesTotal);
  const priorCompleted = Math.max(0, totalCompleted - completionsSeriesTotal);
  let cumulativePlaced = priorPlaced;
  let cumulativeCompleted = priorCompleted;
  const chartData = placements.map((p) => {
    cumulativePlaced += p.placedInTulsa;
    cumulativeCompleted += completedByQuarter.get(p.quarter) ?? 0;
    const rate = cumulativeCompleted > 0 ? (cumulativePlaced / cumulativeCompleted) * 100 : 0;
    return {
      quarter: p.quarter,
      placedInTulsa: p.placedInTulsa,
      cumulativePlaced,
      cumulativeCompleted,
      placementRate: rate,
    };
  });
  const maxRate = Math.max(goalRate, ...chartData.map((p) => p.placementRate));

  return (
    <CardShell
      title="Job Placement"
      subtitle={cur.quarter}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Unique individuals placed in jobs in Tulsa this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur.placedInTulsa}
                </Text>
              ),
              sublabel: qoqPlacedText,
            },
            {
              label: 'YTD',
              labelTooltip: 'Calendar year to date placements (sum of quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {ytdPlaced === null ? '—' : ytdPlaced}
                </Text>
              ),
              sublabel: yoyPlacedText,
            },
            {
              label: 'All-Time Placements',
              labelTooltip: 'Total unique individuals placed in jobs in Tulsa all-time (includes periods not shown).',
              value: (
                <Text size="lg" weight={800}>
                  {totalPlaced}
                </Text>
              ),
            },
            {
              label: 'Placement Rate',
              labelTooltip: 'Percent of program completers placed in a job in Tulsa (all-time).',
              value: (
                <Text size="lg" weight={800}>
                  {Math.round(placementRate)}%
                </Text>
              ),
              sublabel: `Goal ${goalRate}%`,
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Cumulative Placement Rate
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis dataKey="quarter" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
                domain={[0, Math.min(100, Math.ceil(maxRate))]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const p: any = payload[0]?.payload;
                  const v = p?.placementRate;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                        <div style={{ fontWeight: 900, fontSize: 14 }}>{typeof v === 'number' ? Math.round(v) : '—'}% Placement Rate</div>
                        <div style={{ opacity: 0.9 }}>
                          {p?.cumulativePlaced ?? '—'} placed out of {p?.cumulativeCompleted ?? '—'} completions
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
              <ReferenceLine y={goalRate} stroke="#fa5252" strokeDasharray="4 4" ifOverflow="extendDomain" />
              <Line
                type="monotone"
                dataKey="placementRate"
                name="Cumulative Placement Rate"
                stroke={theme.colors.blue[6]}
                strokeWidth={2}
                dot={(dotProps: any) => {
                  const pointKey = dotProps?.payload?.quarter ?? dotProps?.index ?? `${dotProps?.cx}-${dotProps?.cy}`;
                  return (
                    <circle
                      key={`thec-job-placement-dot-${pointKey}`}
                      cx={dotProps.cx}
                      cy={dotProps.cy}
                      r={2}
                      fill={theme.colors.blue[6]}
                      stroke={theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'}
                      strokeWidth={1.5}
                    />
                  );
                }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
